// Highlighted-dossier hero — the dark-forest "highlighted dossier"
// presentation from the Epic 8 Shop handoff, extracted so the SAME markup +
// capabilities render in two places: the shop page's standing hero (the
// admin-highlighted drop) and the pop-up that opens when a standing card is
// clicked. Both instances are pixel-identical and both can purchase, because
// this component owns the creator marquee AND the real buy flow (ShopBuyModal
// → useDossierPurchase). Rendering the hero for any drop is a matter of
// passing that drop in.

import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { Button, SealedDossier, TierBadge } from "@greenhold/components";

import { ShopBuyModal } from "./ShopBuyModal";
import type { ShopDropResponse } from "./useShopDrops";
import { useDropForPurchase } from "../dossier-purchase/useDropForPurchase";

const EUR = new Intl.NumberFormat("en-IE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
});

function formatEur(cents: number): string {
  return EUR.format(cents / 100);
}

const HERO_TIERS = ["common", "preferred", "premium", "founders"] as const;

// Creator-marquee scroll speed. Tunable in one place: the strip travels one
// full single-copy width (translateX -50% across the duplicated track) over
// this many seconds. Bumped from the original 45s to a calm, readable pace
// (half speed) per owner feedback 2026-08-14. The reduced-motion media query
// below still overrides this to `none` outright.
const MARQUEE_DURATION_SECONDS = 90;

export interface HighlightedDossierProps {
  drop: ShopDropResponse;
}

/** The dark-forest highlighted-dossier hero for a single drop, including its
 *  creator marquee and the real "Buy sealed" purchase flow. Reused verbatim
 *  by the shop hero and the standing-card pop-up. */
export function HighlightedDossier({ drop }: HighlightedDossierProps) {
  const [buyOpen, setBuyOpen] = useState(false);

  // Fetch the drop's detail for real creator portraits + handles (the browse
  // payload only exposes `first_creator_names`).
  const detail = useDropForPurchase(drop.drop_id);

  const marquee = useMemo(() => {
    const fromDetail = detail.data?.creators ?? [];
    if (fromDetail.length > 0) {
      return fromDetail.map((c) => ({
        name: c.display_name || (c.handle ? `@${c.handle}` : ""),
        img: c.image_signed_url,
      }));
    }
    // Graceful degradation: the browse payload gives names only → initials.
    return (drop.first_creator_names ?? []).map((name) => ({
      name,
      img: null as string | null,
    }));
  }, [detail.data, drop.first_creator_names]);

  // Marquee only animates when the roster overflows the visible width.
  // Few-enough creators that they all fit → render them static (no scroll,
  // no duplication). We measure the single-copy track width against the
  // viewport (dividing by 2 when the duplicated/animating copy is mounted,
  // so the comparison stays stable across the static↔scroll toggle).
  const marqueeViewportRef = useRef<HTMLDivElement>(null);
  const marqueeTrackRef = useRef<HTMLDivElement>(null);
  const [marqueeOverflow, setMarqueeOverflow] = useState(false);

  useLayoutEffect(() => {
    const viewport = marqueeViewportRef.current;
    const track = marqueeTrackRef.current;
    if (!viewport || !track) {
      setMarqueeOverflow(false);
      return;
    }
    const measure = () => {
      // When overflowing we render two copies; halve to recover the
      // intrinsic single-copy width for a stable comparison.
      const singleWidth = marqueeOverflow
        ? track.scrollWidth / 2
        : track.scrollWidth;
      setMarqueeOverflow(singleWidth > viewport.clientWidth + 4);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(viewport);
    ro.observe(track);
    return () => ro.disconnect();
  }, [marquee, marqueeOverflow]);

  return (
    <>
      <style>{`
@keyframes gh-shop-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@media (prefers-reduced-motion: reduce) { .gh-shop-marquee-track { animation: none !important; } }
`}</style>
      <section className="flex flex-wrap items-center gap-12 rounded-xl border border-brand-gold-500/40 bg-brand-forest-800 p-10">
        <div className="w-[200px] shrink-0">
          <SealedDossier
            size="md"
            state="selected"
            title={drop.portfolio_title}
            game={drop.portfolio_theme ?? undefined}
            showRetirementWarning={drop.status === "retiring"}
            sealColor={drop.portfolio_seal_color}
          />
        </div>

        <div className="grid min-w-[280px] flex-1 gap-3.5">
          <span className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-brand-gold-300">
            Highlighted dossier
          </span>
          <h2 className="m-0 font-heading text-2xl text-brand-paper-100">
            {drop.portfolio_title}
          </h2>
          {drop.portfolio_description ? (
            <p className="m-0 max-w-[520px] whitespace-pre-line font-body text-[0.88rem] text-brand-paper-100/75">
              {drop.portfolio_description}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2.5">
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.1em] text-brand-paper-100/60">
              Tiers inside
            </span>
            {HERO_TIERS.map((t) => (
              <TierBadge key={t} tier={t} size="sm" />
            ))}
          </div>

          {marquee.length > 0 ? (
            <div
              ref={marqueeViewportRef}
              className="overflow-hidden"
              style={
                marqueeOverflow
                  ? {
                      maskImage:
                        "linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent)",
                      WebkitMaskImage:
                        "linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent)",
                    }
                  : undefined
              }
            >
              <div
                ref={marqueeTrackRef}
                className="gh-shop-marquee-track flex w-max"
                style={
                  marqueeOverflow
                    ? {
                        animation: `gh-shop-marquee ${MARQUEE_DURATION_SECONDS}s linear infinite`,
                      }
                    : undefined
                }
              >
                {(marqueeOverflow ? [...marquee, ...marquee] : marquee).map(
                  (c, i) => (
                    <div
                      key={`${c.name}-${i}`}
                      className="mr-6 grid justify-items-center gap-1.5"
                    >
                      {c.img ? (
                        <img
                          src={c.img}
                          alt=""
                          className="h-14 w-14 rounded-full object-cover"
                        />
                      ) : (
                        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-gold-500/20 font-heading text-lg text-brand-gold-300">
                          {c.name.replace(/^@/, "").charAt(0).toUpperCase() || "?"}
                        </span>
                      )}
                      <span className="font-mono text-[0.58rem] uppercase tracking-[0.08em] text-brand-paper-100/60">
                        {c.name}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </div>
          ) : null}

          <div className="mt-1.5 flex flex-wrap items-center gap-4">
            <Button
              variant="primary"
              size="lg"
              onClick={() => setBuyOpen(true)}
              data-testid="shop-hero-buy"
            >
              Buy sealed · {formatEur(drop.price_cents)}
            </Button>
          </div>
        </div>
      </section>

      <ShopBuyModal
        open={buyOpen}
        onClose={() => setBuyOpen(false)}
        dropId={drop.drop_id}
        title={drop.portfolio_title}
        priceCents={drop.price_cents}
        perUserCap={detail.data?.per_user_cap ?? null}
      />
    </>
  );
}
